// export default Slide4;
import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import ReactTable from "react-table-v6";
import Loader from "../../../../../../settings/loader";
import { formatDate } from "../../../../../../formatDate";
import commaNumber from "comma-number";
import "react-table-v6/react-table.css";
import "./table-style.scss";

function Slide4(props) {
  const location = useLocation();
  const [tableData, setTableData] = useState({
    result: null,
    table: [],
  });
  const [load, setLoad] = useState(0);

  // Initialize result when props.data and filter are ready
  useEffect(() => {
    if (props.data && !load) {
      setTableData((prev) => ({
        ...prev,
        result: {
          ...props.filter.data,
          ...props.data,
        },
      }));
      setLoad(1);
    }
    // eslint-disable-next-line
  }, [props.data, props.filter]);

  // Prepare table data when result is set
  useEffect(() => {
    if (load === 1 && tableData.result) {
      const filteredTable =
        tableData.result.sales.data
          .filter((sale) => !sale.payType.includes("Void"))
          .sort(sortSales)
          .map((data) => {
            const chain = tableData.result.chains.find(
              (chain) => chain.chaKey === data.chaKey
            );
            const brand = tableData.result.brands.find(
              (brand) => brand.braKey === data.braKey
            );

            return {
              ...data,
              businessName:
                (brand ? brand.name : "") + " - " + (chain ? chain.name : ""),
              currency: chain?.currency?.toUpperCase() || "QAR",
            };
          }) || [];
      setTableData((prev) => ({
        ...prev,
        table: filteredTable,
      }));
      setLoad(2);
    }
    // eslint-disable-next-line
  }, [load, tableData.result]);

  // Export logic
  useEffect(() => {
    if (props.export && tableData.table.length) {
      props.getData({
        type: "table",
        title: "Group Sales by Checks",
        filter: {
          brands: props.filter.data.brands.map((brand) => brand.name),
          chains: props.filter.data.chains.map((chain) => chain.name),
          revCent: props.filter.data.revCent.map((revCent) => revCent.name),
          payType: props.filter.data.payType.map((payType) => payType.name),
          date: {
            start: props.filter.data.date.start,
            end: props.filter.data.date.end,
          },
        },
        body: [
          {
            head: [
              [
                "Status",
                "Bill No.",
                "Total",
                "Revenue Center",
                "Payment",
                "Customers",
                "Session No.",
                "Date / Time",
                "Business Name",
                "Note",
              ],
            ],
            body: tableData.table.map((value) => [
              value.status ? "Closed" : "Open",
              value.billNo,
              `${value?.currency} ${commaNumber(value.total.toFixed(2))}`,
              value.revCent,
              value.payType.map((payType) => payType.name).join(" "),
              commaNumber(value.customers),
              value.sesNo,
              formatDate.long(value.date),
              value.businessName,
              value.note ? value.note : "",
            ]),
          },
        ],
      });
    }
    // eslint-disable-next-line
  }, [props.export, tableData.table]);

  function sortSales(a, b) {
    if (parseInt(a.date) > parseInt(b.date)) return -1;
    if (parseInt(a.date) < parseInt(b.date)) return 1;
    return 0;
  }

  // Unique business names for filter dropdown
  const uniqueNames = useMemo(() => {
    if (!tableData.table) return [];
    return [...new Set(tableData.table.map((table) => table.businessName))];
  }, [tableData.table]);

  // Render link for table cells
  const renderLink = (component) => (
    <Link
      to={`/${location.pathname.split("/")[1]}/view/${
        location.pathname.split("/")[2]
      }/${component.data._id}`}
      state={{ prevUrl: location.pathname }}
    >
      {component.value}
    </Link>
  );

  // Table columns
  const columns = [
    {
      Header: "Business Name",
      accessor: "businessName",
      filterMethod: (filter, row) => {
        if (filter.value === "all") return true;
        return row[filter.id] === filter.value;
      },
      Filter: ({ filter, onChange }) => (
        <select
          onChange={(event) => onChange(event.target.value)}
          style={{ width: "100%" }}
          value={filter ? filter.value : "all"}
        >
          <option value="all">All</option>
          {uniqueNames.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      ),
      Cell: (row) => renderLink({ data: row.row._original, value: row.value }),
    },
    {
      Header: "Status",
      accessor: "status",
      filterMethod: (filter, row) => {
        if (filter.value === "all") return true;
        if (filter.value === "Open") return row[filter.id] === 0;
        if (filter.value === "Closed") return row[filter.id] === 1;
        return true;
      },
      Filter: ({ filter, onChange }) => (
        <select
          onChange={(event) => onChange(event.target.value)}
          style={{ width: "100%" }}
          value={filter ? filter.value : "all"}
        >
          <option value="all">All</option>
          <option value="Open">Open</option>
          <option value="Closed">Closed</option>
        </select>
      ),
      Cell: (row) =>
        renderLink({
          data: row.row._original,
          value: (
            <div className={"name"}>
              <div className={row.value ? "open" : ""} />
              {row.value ? "Closed" : "Open"}
            </div>
          ),
        }),
    },
    {
      Header: "Bill No.",
      accessor: "billNo",
      Cell: (row) => renderLink({ data: row.row._original, value: row.value }),
    },
    {
      Header: "Total",
      accessor: "total",
      Cell: (row) =>
        renderLink({
          data: row.row._original,
          value: `${row?.row?._original?.currency} ${commaNumber(
            row.value.toFixed(2)
          )}`,
        }),
    },
    {
      Header: "Revenue Center",
      accessor: "revCent",
      Cell: (row) => renderLink({ data: row.row._original, value: row.value }),
    },
    {
      id: "payment",
      Header: "Payment",
      accessor: (data) => {
        const totalPayment = data.payType.reduce((total, payType) => {
          return total + payType.amount;
        }, 0);
        return parseFloat(totalPayment);
      },
      Cell: (row) =>
        renderLink({
          data: row.row._original,
          value: row.row._original.payType.map((payType, index) => (
            <div key={index}>{`${payType.name} - ${
              row?.original?.currency
            } ${commaNumber(payType.amount.toFixed(2))}`}</div>
          )),
        }),
    },
    {
      Header: "Customers",
      accessor: "customers",
      Cell: (row) => renderLink({ data: row.row._original, value: row.value }),
    },
    {
      Header: "Session No.",
      accessor: "sesNo",
      Cell: (row) => renderLink({ data: row.row._original, value: row.value }),
    },
    {
      Header: "Date / Time",
      accessor: "date",
      Cell: (row) =>
        renderLink({
          data: row.row._original,
          value: formatDate.long(row.value),
        }),
    },
    {
      Header: "Note",
      accessor: "note",
      Cell: (row) => renderLink({ data: row.row._original, value: row.value }),
    },
  ];

  return (
    <div>
      {load < 2 ? (
        <Loader />
      ) : (
        <ReactTable
          className="-striped -highlight"
          data={tableData.table}
          columns={columns}
          sortable
          filterable
          getTdProps={() => ({
            style: {
              textAlign: "center",
            },
          })}
        />
      )}
    </div>
  );
}

export default Slide4;
